/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-05-16 15:26:55
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { FullscreenBar, Heading, Select } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import RenderCollapse from 'src/components/views/subscription-v4/feature-collapsible';
import ActionChoosePackage from 'src/components/views/subscription-v4/package/action-choose-package';
import { routes } from 'src/routes';
import {
  FEATURE_COMPARISON_V4,
  DATA_BY_FEATURE_V4,
  PACKAGES_V4,
  LARGE_PRODUCT_LABEL,
  REQUEST_LARGE_PLAN
} from 'src/constants/subscription';
import history from 'src/routes/history';
import { formatNumberInThousand } from 'src/helpers';
import { enableNewPricingPageExceededUser } from 'src/helpers/subscription-v3-helper';
import {
  exitSABFullscreen,
  enterSABFullscreen
} from 'src/integrations/shopify-app-bridge/fullscreen';
import * as subscriptionV3Action from 'src/stores/actions/subscription-v3-actions';
import { LARGE_PRODUCT } from 'src/constants/subscription';
import { getCorrectPlanV4 } from 'src/helpers/subscription-v3-helper';

const getOptionsSelectProduct = (packagePlans = [], currentTotalProducts) => {
  // ...
  // {name: "basic_regular_45", term: "Up to 45,000 Products", products: 45000, price: 299}
  // {name: "basic_regular_50", term: "Up to 50,000 Products", products: 50000, price: 329}
  // {name: "basic_regular_75", term: "Up to 75,000 products", products: 75000, price: 479}
  // {name: "basic_regular_100", term: "Up to 100,000 products", products: 100000, price: 629}
  // ...

  const packagePlanExceed =
    currentTotalProducts > LARGE_PRODUCT
      ? _.find(packagePlans, plan => plan.products > currentTotalProducts)
      : _.find(packagePlans, plan => plan.products >= LARGE_PRODUCT);

  const options = [
    ..._.map(
      _.filter(packagePlans, plan => plan.products <= LARGE_PRODUCT),
      item => ({
        value: `${item.products}`,
        name: item.name,
        label: formatNumberInThousand(item.products)
      })
    ),
    {
      value: `${formatNumberInThousand(LARGE_PRODUCT)}+`,
      name: REQUEST_LARGE_PLAN,
      label: `${formatNumberInThousand(LARGE_PRODUCT)}+`
    }
  ];

  // {value: '40000', name: 'basic_regular_40', label: '40,000'}
  // {value: '45000', name: 'basic_regular_45', label: '45,000'}
  // {value: LARGE_PRODUCT_LABEL, name: REQUEST_LARGE_PLAN, label: LARGE_PRODUCT_LABEL}
  return [options, packagePlanExceed];
};

//--------------------------------
const PackageComparison = props => {
  const {
    i18n,
    packagePlans,
    getDetailInvoice,
    getPackagePlans,
    confirmationUrl,
    invoice,
    tenantApp,
    currentTotalProducts,
    correctPlan
  } = props;
  const { products } = props.match.params;

  const enableForExceededUser = enableNewPricingPageExceededUser(tenantApp);

  // Handle back to subscription page
  const handleBack = () => {
    history.push(routes.subscription.plan());
    exitSABFullscreen();
  };

  useEffect(() => {
    const unsubscribeExit = enterSABFullscreen(handleBack);

    // Get detail invoice
    if (_.isEmpty(invoice) || !invoice) {
      getDetailInvoice();
    }
    // Get package list
    if (!_.get(packagePlans, 'basic', []).length) {
      getPackagePlans();
    }

    return () => {
      unsubscribeExit();
    };
  }, [invoice]);

  useEffect(() => {
    // Redirect to shopify confirmation page
    if (confirmationUrl) {
      window.top.location.href = confirmationUrl;
      return;
    }
  }, [confirmationUrl]);

  const [options, productNumberExceed] = getOptionsSelectProduct(
    packagePlans['basic'],
    currentTotalProducts
  );
  const currentProduct = products === LARGE_PRODUCT_LABEL ? productNumberExceed.products : products;

  const setPackageSelected = useCallback(products => {
    history.push(routes.subscription.comparison(products));
  }, []);

  useEffect(() => {
    if (enableForExceededUser) {
      const backButton = document.querySelector('.Polaris-FullscreenBar__BackAction');
      if (backButton?.childNodes[1]) {
        backButton.childNodes[1].textContent = i18n.translate('AdminHTML.Common.Action.exit');
      }
    }
  }, []);

  const correctPlanName = _.get(correctPlan, 'name', '');

  const indexCurrentPlan = useMemo(() => {
    if (correctPlanName.includes('basic')) return 0;
    if (correctPlanName.includes('essential')) return 1;
    if (correctPlanName.includes('professional')) return 2;
  }, [correctPlan]);

  const isDisabled = products !== LARGE_PRODUCT_LABEL && currentTotalProducts > parseInt(products);

  return (
    <div className="BC-Page BC-Page__Package-ComparisonV4">
      <FullscreenBar onAction={handleBack}>
        <div className="title-bar">
          <b className="title">
            {i18n.translate('AdminHTML.SubscriptionV4.PackageComparison.title')}
          </b>
        </div>
      </FullscreenBar>

      <div>
        <div className="align-center">
          {enableForExceededUser && (
            <Heading>
              <div className="choose-product-number">
                <div className="title">
                  <b>{i18n.translate('AdminHTML.SubscriptionV4.PackageComparison.subTitle')}</b>
                </div>
                <div className="select-product">
                  <Select
                    options={options}
                    // disabled={hasPackageRequest}
                    value={products}
                    onChange={e => setPackageSelected(e)}
                  />
                  <p>{i18n.translate('AdminHTML.SubscriptionV4.Common.products')}</p>
                </div>
              </div>
            </Heading>
          )}
        </div>
        {/* Render table comparison */}
        <div className="package-comparison-table">
          <div className="row row-header">
            <div className="column" />
            {PACKAGES_V4.map((info, index) => {
              const isCurrentPlan = correctPlanName.includes(info.name);
              const isMostPopular =
                !correctPlanName.includes('essential') && info.name === 'essential';
              const currentPackagePlans = packagePlans[info.name];

              const disableFreemium = info.name === 'freemium' && currentTotalProducts > 25;
              return (
                <div
                  key={info.name + index}
                  className={`column column-package ${
                    isMostPopular ? 'column-package--success' : ''
                  }`}>
                  {isCurrentPlan && (
                    <div className="package-tag package-tag-subdued">
                      {i18n.translate('AdminHTML.SubscriptionV4.currentPlan')}
                    </div>
                  )}

                  {isMostPopular && (
                    <div className="package-tag package-tag-success">
                      {i18n.translate('AdminHTML.SubscriptionV4.mostPopular')}
                    </div>
                  )}
                  <p>{i18n.translate(info.title)}</p>
                  {/* {enableForExceededUser && ( */}
                  <div className="price">
                    <span>
                      $
                      {info.name === 'freemium' && currentPackagePlans
                        ? currentPackagePlans[0]?.price
                        : currentPackagePlans?.find(e => +e.products >= +currentProduct)?.price}
                    </span>
                    <span>{i18n.translate('AdminHTML.SubscriptionV4.Common.perMonth')}</span>
                  </div>
                  {/* )} */}
                  <ActionChoosePackage
                    packageInfo={info}
                    primary={
                      (!isCurrentPlan && index > indexCurrentPlan) || // Non-Professional primary when index btn > index cur;
                      (indexCurrentPlan === 2 && index === 2) // Professional primary when current is professiona;
                    }
                    products={parseInt(currentProduct)}
                    packagePlans={packagePlans[info.name]}
                    allPackagePlans={packagePlans}
                    packageSelected={products}
                    isForExpireExceed={enableForExceededUser}
                    disabled={isDisabled || disableFreemium}
                    index={index}
                  />
                </div>
              );
            })}
          </div>
          <div className="collapse-table">
            {FEATURE_COMPARISON_V4.map((feature, index) => (
              <RenderCollapse
                key={`feature-${index}`}
                feature={feature}
                data={DATA_BY_FEATURE_V4[feature]}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getPackagePlans: () => dispatch(subscriptionV3Action.getPackagePlans(ownProps.i18n)),
  getDetailInvoice: () => dispatch(subscriptionV3Action.getDetailInvoice(ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const packagePlans = _.get(state.subscriptionV3, 'packagePlans', {});
  const currentTotalProducts = _.get(tenantApp, 'appParams.currentTotalProducts');
  const chargeInfo = _.get(state.subscriptionV3, 'invoice.chargeInfo', {});
  const selectedPlan = _.get(state.oauth.tenantApp, 'selectedPlan', '');
  const correctPlan = getCorrectPlanV4(
    chargeInfo,
    packagePlans,
    currentTotalProducts,
    selectedPlan
  );

  return {
    tenantApp,
    invoice: _.get(state.subscriptionV3, 'invoice', {}),
    confirmationUrl: _.get(state.subscriptionV3, 'confirmationUrl', ''),
    packagePlans,
    currentTotalProducts,
    correctPlan
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(PackageComparison));
